import { FC, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import { useGame } from '@/providers'
import { useAuth } from '@/hooks'
import { useWalletStore, useGameStore } from '@/store'

// Inline SVG icons for input mode toggle
const KeyboardIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
)

const MicrophoneIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
)

export const TitleScreen: FC = () => {
  const navigate = useNavigate()
  const { connected } = useWallet()
  const { setVisible } = useWalletModal()
  const { publicKey } = useWalletStore()
  const { session, isLoading, voiceModeEnabled, toggleVoiceMode, error: gameError } = useGameStore()
  const { startNewGame } = useGame()
  const { isAuthenticated, isAuthenticating, userProfile, authenticate, error: authError } = useAuth()

  const [isStarting, setIsStarting] = useState(false)

  const handleConnectWallet = () => {
    setVisible(true)
  }

  const handleAuthenticate = async () => {
    if (!connected) {
      setVisible(true)
      return
    }
    await authenticate()
  }

  const handleNewGame = async () => {
    if (!connected) {
      setVisible(true)
      return
    }

    // Ensure user is authenticated first
    if (!isAuthenticated) {
      const success = await authenticate()
      if (!success) return
    }

    setIsStarting(true)
    try {
      await startNewGame('diamond_hands')
      // Only navigate if session was actually created
      const currentSession = useGameStore.getState().session
      if (currentSession) {
        navigate('/game')
      }
    } finally {
      setIsStarting(false)
    }
  }

  const handleContinue = () => {
    navigate('/game')
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="font-pixel text-pixel-2xl text-game-accent mb-2 tracking-wider">
          TRENCH QUEST
        </h1>
        <p className="font-retro text-xl text-game-muted">
          A Solana Trading Adventure
        </p>
      </div>

      {/* ASCII Art Logo */}
      <pre className="font-mono text-game-accent text-xs mb-8 hidden sm:block">
{`
  ████████╗██████╗ ███████╗███╗   ██╗ ██████╗██╗  ██╗
  ╚══██╔══╝██╔══██╗██╔════╝████╗  ██║██╔════╝██║  ██║
     ██║   ██████╔╝█████╗  ██╔██╗ ██║██║     ███████║
     ██║   ██╔══██╗██╔══╝  ██║╚██╗██║██║     ██╔══██║
     ██║   ██║  ██║███████╗██║ ╚████║╚██████╗██║  ██║
     ╚═╝   ╚═╝  ╚═╝╚══════╝╚═╝  ╚═══╝ ╚═════╝╚═╝  ╚═╝
               ██████╗ ██╗   ██╗███████╗███████╗████████╗
              ██╔═══██╗██║   ██║██╔════╝██╔════╝╚══██╔══╝
              ██║   ██║██║   ██║█████╗  ███████╗   ██║
              ██║▄▄ ██║██║   ██║██╔══╝  ╚════██║   ██║
              ╚██████╔╝╚██████╔╝███████╗███████║   ██║
               ╚══▀▀═╝  ╚═════╝ ╚══════╝╚══════╝   ╚═╝
`}
      </pre>

      {/* Main Menu */}
        <RetroWindow className="w-full max-w-md">
          <div className="space-y-4">
            {/* Two-Step Authentication Flow */}
            <div className="space-y-4">
              {/* Step 1: Connect Wallet */}
              <div className={`p-3 border-2 ${connected ? 'border-game-profit bg-game-profit/10' : 'border-game-border'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center border-2 font-pixel text-sm ${
                    connected ? 'border-game-profit bg-game-profit text-game-bg' : 'border-game-border text-game-muted'
                  }`}>
                    {connected ? '✓' : '1'}
                  </div>
                  <div className="flex-1">
                    <PixelText size="xs" color={connected ? 'profit' : 'default'}>
                      {connected ? 'WALLET CONNECTED' : 'CONNECT WALLET'}
                    </PixelText>
                    {connected && publicKey ? (
                      <p className="font-mono text-xs text-game-muted truncate">
                        {publicKey.slice(0, 8)}...{publicKey.slice(-8)}
                      </p>
                    ) : (
                      <p className="font-retro text-xs text-game-muted">
                        Needed to save your game
                      </p>
                    )}
                  </div>
                  {!connected && (
                    <RetroButton
                      variant="primary"
                      size="sm"
                      onClick={handleConnectWallet}
                    >
                      CONNECT
                    </RetroButton>
                  )}
                </div>
              </div>

              {/* Step 2: Sign Message */}
              <div className={`p-3 border-2 ${
                isAuthenticated
                  ? 'border-game-profit bg-game-profit/10'
                  : connected
                    ? 'border-game-gold bg-game-gold/10'
                    : 'border-game-border opacity-50'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center border-2 font-pixel text-sm ${
                    isAuthenticated
                      ? 'border-game-profit bg-game-profit text-game-bg'
                      : connected
                        ? 'border-game-gold bg-game-gold text-game-bg animate-pulse'
                        : 'border-game-border text-game-muted'
                  }`}>
                    {isAuthenticated ? '✓' : '2'}
                  </div>
                  <div className="flex-1">
                    <PixelText size="xs" color={isAuthenticated ? 'profit' : connected ? 'gold' : 'muted'}>
                      {isAuthenticated ? 'SIGNED IN' : 'VERIFY OWNERSHIP'}
                    </PixelText>
                    <p className="font-retro text-xs text-game-muted">
                      {isAuthenticated
                        ? userProfile
                          ? `Games: ${userProfile.gamesPlayed} | Wins: ${userProfile.totalWins}`
                          : 'Ready to play!'
                        : 'Sign a message to prove you own this wallet'}
                    </p>
                  </div>
                  {connected && !isAuthenticated && (
                    <RetroButton
                      variant="primary"
                      size="sm"
                      onClick={handleAuthenticate}
                      disabled={isAuthenticating}
                      className="animate-pulse"
                    >
                      {isAuthenticating ? 'SIGNING...' : 'SIGN'}
                    </RetroButton>
                  )}
                </div>
              </div>

              {/* Info text when wallet connected but not signed */}
              {connected && !isAuthenticated && !isAuthenticating && (
                <div className="p-2 bg-game-gold/10 border border-game-gold/30 text-center">
                  <p className="font-retro text-xs text-game-gold">
                    Signing verifies wallet ownership. No gas fees, no transaction.
                  </p>
                </div>
              )}
            </div>

            {/* Error Display */}
            {(authError || gameError) && (
              <div className="p-2 bg-game-loss/20 border border-game-loss">
                <PixelText size="xs" color="loss">
                  {authError || gameError}
                </PixelText>
              </div>
            )}

            {/* Menu Buttons - Only show when authenticated */}
            {isAuthenticated && (
              <div className="space-y-3 pt-4 border-t border-game-border">
                {session && session.status === 'active' && (
                  <RetroButton
                    variant="primary"
                    size="lg"
                    className="w-full"
                    onClick={handleContinue}
                  >
                    CONTINUE GAME
                  </RetroButton>
                )}

                <RetroButton
                  variant={session ? 'default' : 'primary'}
                  size="lg"
                  className="w-full"
                  onClick={handleNewGame}
                  disabled={isLoading || isStarting}
                >
                  {isStarting ? 'STARTING...' : isLoading ? 'LOADING...' : 'NEW GAME'}
                </RetroButton>

                <RetroButton
                  variant="default"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate('/settings')}
                >
                  SETTINGS
                </RetroButton>

                {/* Input Mode Toggle */}
                <div className="pt-3 border-t border-game-border">
                  <div className="flex items-center justify-between">
                    <span className="font-retro text-sm text-game-muted">
                      Input Mode
                    </span>
                    <button
                      onClick={toggleVoiceMode}
                      className="flex items-center gap-1 p-1 border-2 border-game-border bg-game-panel"
                    >
                      <div
                        className={`flex items-center justify-center w-10 h-8 transition-colors ${
                          !voiceModeEnabled
                            ? 'bg-game-accent text-game-bg'
                            : 'text-game-muted hover:text-game-text'
                        }`}
                      >
                        <KeyboardIcon className="w-5 h-5" />
                      </div>
                      <div
                        className={`flex items-center justify-center w-10 h-8 transition-colors ${
                          voiceModeEnabled
                            ? 'bg-game-accent text-game-bg'
                            : 'text-game-muted hover:text-game-text'
                        }`}
                      >
                        <MicrophoneIcon className="w-5 h-5" />
                      </div>
                    </button>
                  </div>
                  <p className="font-retro text-xs text-game-muted mt-1">
                    {voiceModeEnabled ? 'Voice: Speak your actions' : 'Text: Type your actions'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </RetroWindow>

      {/* Footer */}
      <div className="mt-8 text-center space-y-2">
        <p className="font-retro text-sm text-game-muted">
          Built for Solana Seeker
        </p>
        <p className="font-retro text-xs text-game-muted">
          v0.1.0 - Not Financial Advice
        </p>
        <div className="flex items-center justify-center gap-3 pt-2">
          <Link to="/privacy" className="font-retro text-xs text-game-muted hover:text-game-accent transition-colors">
            Privacy
          </Link>
          <span className="text-game-muted">|</span>
          <Link to="/terms" className="font-retro text-xs text-game-muted hover:text-game-accent transition-colors">
            Terms
          </Link>
          <span className="text-game-muted">|</span>
          <Link to="/license" className="font-retro text-xs text-game-muted hover:text-game-accent transition-colors">
            License
          </Link>
        </div>
      </div>
    </div>
  )
}
